import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input, Button as AriaButton, TextField, Pressable } from 'react-aria-components';
import { autosuggestUnbxd } from 'actions/search';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { updateQuerystringParam, getLang, getMarket, getRealLanguage, getCookie, COOKIES, getIsMobile, getUnbxdTranslations } from 'utils/helpers';

import { useI18n } from 'context/i18n';
import { useIsMounted } from 'hooks';

import { CloseSVG } from 'components/iconsSVG/close';
import { Button } from 'components/ui/button';
import PersonalShopper from './personalShopper';
import AutoSuggest from './autoSuggest';

import { getDomainValue } from 'utils/domain';
import styles from './style.module.css';
import { Label } from 'components/ui/field';

const minSearchLength = 1;
let timeoutHandler: ReturnType<typeof setTimeout> | null = null;
let blurElementTimeout: ReturnType<typeof setTimeout> | null = null;

export function SearchTNM() {
  const { t, locale, setSearchQuery, searchQueryShadow, setSearchQueryShadow, profileData, unbxdSearchResults, setUnbxdSearchResults } = useI18n();
  const [value, setValue] = useState('');
  const [expandedSearch, setExpandedSearch] = useState(false);
  const [autosuggestVisible, setAutosuggestVisible] = useState(false);
  const isMounted = useIsMounted();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const isMobile = getIsMobile('767.5px');

  const unbxdTranslations = useMemo(() => getUnbxdTranslations(t), [t]);
  const newSearch = unbxdTranslations !== false;
  const hasPersonalShoppper = profileData?.config?.image_search === true;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchValue = params.get('searchTerm');
    const searchMode = params.get('mode');

    if (searchValue && searchValue !== '' && searchMode !== 'completeLook') {
      setValue(searchValue);
    }
    window.triggerSearch = (searchStr: string) => {
      if (!isMounted()) return;

      if (!window.location.pathname.includes('/search')) {
        window.location.assign(`${window.location.origin}/${locale}/search?searchTerm=${searchStr}`);
      } else {
        updateQuerystringParam(window.location.search, 'searchTerm', searchStr);
        setSearchQuery(searchStr);
        setValue(searchStr);
        // document.querySelector('#unbxd-search-box').value = searchStr;
      }
    };

    window.hideAutosuggest = () => {
      setAutosuggestVisible(false);
    };
  }, []);

  const checkIsOutsideSearch = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    const isOutsideSearch = target?.closest('#search-test-b') === null;

    if (isOutsideSearch) {
      onClickExpandIcon();
      setAutosuggestVisible(false);
    }
  };

  const checkAutosuggestInViewport = () => {
    const autoSuggestWidget = document.getElementById('autosuggest-widget');

    if (autoSuggestWidget) {
      const dimensions = autoSuggestWidget.getBoundingClientRect();
      const { top, bottom } = dimensions;

      const isVisible = isMobile ? bottom >= 10 : top >= -10;

      if (!isVisible) {
        onClickExpandIcon();
        blurActiveElement();
        setAutosuggestVisible(false);
      }
    }
  };

  useEffect(() => {
    if (autosuggestVisible === true) {
      document.addEventListener('mouseup', checkIsOutsideSearch);
      document.addEventListener('scroll', checkAutosuggestInViewport);
      // document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('mouseup', checkIsOutsideSearch);
      document.removeEventListener('scroll', checkAutosuggestInViewport);
      // document.body.style.overflow = 'inherit';
    }

    return () => {
      document.removeEventListener('mouseup', checkIsOutsideSearch);
      document.removeEventListener('scroll', checkAutosuggestInViewport);
    };
  }, [autosuggestVisible]);

  const blurActiveElement = () => {
    (document.activeElement as HTMLInputElement)?.blur();
  };

  const letsSearch = useCallback(
    async (locale: string, searchStr: string) => {
      if (!searchStr || searchStr.trim().length < minSearchLength) {
        return;
      }
      setSearchQueryShadow(searchStr);

      const lang = getLang(locale);
      const isoLanguage = getRealLanguage(lang);
      const unbxdUid = getCookie([COOKIES.UNBXD_UID]) || '';
      const results = await autosuggestUnbxd(isoLanguage, getMarket(locale), searchStr, unbxdTranslations as string[], unbxdUid as string);
      // llamamos al API de unbxd de autosuggest
      // obtenemos respuesta y mostramos modal autosuggest
      // da CORS, usamos respuesta mockup

      setUnbxdSearchResults(results);

      if (isMobile) {
        if (blurElementTimeout) clearTimeout(blurElementTimeout);
        blurElementTimeout = setTimeout(blurActiveElement, 1000);
      }

      dataLayerHandleEvent({
        event: 'virtualPage',
        pageName: window.location.pathname + window.location.search,
      });
    },
    [setSearchQueryShadow, setUnbxdSearchResults, unbxdTranslations],
  );

  const handleKeyUp = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    const searchStr = (event.currentTarget as HTMLInputElement).value;

    if (event.keyCode === 13) {
      if (!isMounted()) return;

      (event.currentTarget as HTMLInputElement)?.blur();
      if (!window.location.pathname.includes('/search')) {
        window.location.assign(`${window.location.origin}/${locale}/search?searchTerm=${searchStr}`);
      } else {
        setAutosuggestVisible(false);
        updateQuerystringParam(window.location.search, 'searchTerm', searchStr);
        setSearchQuery(searchStr);
      }
    } else {
      if (timeoutHandler) clearTimeout(timeoutHandler);
      timeoutHandler = setTimeout(() => {
        letsSearch(locale, searchStr);
      }, 500);
    }
  };

  const onClickExpandIcon = () => {
    document.removeEventListener('mouseup', checkIsOutsideSearch);
    document.removeEventListener('scroll', checkAutosuggestInViewport);

    const tnmMobile = document.querySelector('.tnm--mobile') as HTMLElement;
    const filterNav = document.querySelector('#plp-filter-nav') as HTMLElement;
    const searchInput = document.querySelector('.tnm--mobile [class*="searchInput"]:not(.tnm__modal *)') as HTMLElement;
    const tnmModal = document.querySelector('.tnm__modal') as HTMLElement;
    let forcedCloseTnm = false;
    if (tnmModal && tnmModal.style.display !== 'none') {
      forcedCloseTnm = true;
      (document.querySelector('.tnm__modal .tnm__close') as HTMLElement)?.click();
    }
    if (tnmMobile) {
      if (tnmMobile.style.height === '54px' || tnmMobile.style.height === '' || forcedCloseTnm) {
        tnmMobile.style.height = '100px';
        setExpandedSearch(true);
        if (inputRef?.current) {
          if (searchInput && searchInput.style) {
            searchInput.style.display = 'flex';
            setAutosuggestVisible(true);
            if (forcedCloseTnm) {
              (document.querySelector('.tnm--mobile [class*="searchInput"]:not(.tnm__modal *)') as HTMLElement).style.top = '0px';
              inputRef.current.focus();
            }
          }
          inputRef.current.focus();
        }
      } else {
        tnmMobile.style.height = '54px';
        setExpandedSearch(false);

        if (searchInput && searchInput.style) {
          searchInput.style.display = 'none';
          setAutosuggestVisible(false);
        }

        if (filterNav?.style?.top === '100px') {
          filterNav.style.top = '50px';
        }
      }
    }
  };

  const onClickSearchIcon = () => {
    if (!window.location.pathname.includes('/search')) {
      window.location.assign(`${window.location.origin}/${locale}/search?searchTerm=${searchQueryShadow}`);
    } else {
      updateQuerystringParam(window.location.search, 'searchTerm', searchQueryShadow);
      setValue(searchQueryShadow);
      setSearchQuery(searchQueryShadow);

      if (typeof window?.webComponents?.closeTnm === 'function') {
        window.webComponents.closeTnm();
      } else {
        window.location.reload();
      }
    }
  };

  const onFocus = () => {
    const currentValue = (document.activeElement as HTMLInputElement)?.value;

    if (currentValue !== value) {
      setValue(currentValue);
    }

    setAutosuggestVisible(true);
  };

  useEffect(() => {
    if (!['', null].includes(value) && searchRef.current !== null) {
      // hacemos esto porque el searchtnm está 3 veces en la web por cosas del tnm
      const sr: any = searchRef.current as any;
      if (typeof sr?.checkVisibility === 'function') {
        const isVisible = sr.checkVisibility();

        if (isVisible) {
          letsSearch(locale, value);
        }
      }
    }
  }, [locale, searchRef, letsSearch, value]);

  // const searchComponent = getDomainValue({
  //   camper: (

  //   ),
  //   nnormal: (
  //     <>
  //       <img className={styles.searchIcon} src="/assets-new/search.svg" width={30} height={30} alt="Search" onClick={onClickExpandIcon} />
  //       <AutoComplete
  //         id="unbxd-search-box"
  //         ref={inputRef}
  //         value={value}
  //         onClick={() => setAutosuggestVisible(true)}
  //         onKeyUp={handleKeyUp}
  //         onChange={setValue}
  //         placeholder={t('generico', 'input.placeholder', 'Search')}
  //         className={styles.searchInput}
  //         virtual={false}
  //         dropdownClassName={styles.searchDropdown}
  //         allowClear={false}
  //         backfill
  //         notFoundContent={null}
  //         showArrow
  //         suffixIcon={<img className={styles.suffixIcon} src="/assets-new/search.svg" width={17} height={17} alt="Search" onClick={onClickSearchIcon} />}
  //         onFocus={onFocus}
  //       ></AutoComplete>
  //       {hasPersonalShoppper ?
  //         <PersonalShopper autosuggestVisible={autosuggestVisible} />
  //       : null}
  //     </>
  //   ),
  //   camperlab: null,
  // });

  return (
    <div className={styles.wrapper}>
      {newSearch ?
        <>
          <div
            id="search-test-b"
            ref={searchRef}
            className={` ${styles.container} ${expandedSearch ? styles.expanded : styles.hidden} nnormal:md:w-auto nnormal:md:max-w-80`}
          >
            <div className="relative w-full">
              {!autosuggestVisible && (
                <Pressable onPress={onClickExpandIcon}>
                  <div>
                    <img className={styles.searchIcon} src="/assets-new/search.svg" width={30} height={30} alt="Search" />
                  </div>
                </Pressable>
              )}
              <TextField
                data-element="search-input"
                ref={inputRef}
                value={value}
                onKeyUp={handleKeyUp}
                onChange={setValue}
                onFocus={onFocus}
                className={`${styles.searchInput} flex h-[34px] rounded-2xl border border-solid border-[#919191] !px-3 transition-all nnormal:rounded-none nnormal:border-x-transparent nnormal:border-t-transparent nnormal:!px-0 camper:focus-within:bg-[#f6f6f6] ${autosuggestVisible ? 'camper:w-[90%]' : 'w-full'}`}
              >
                <Label className="sr-only">{t('generico', 'input.placeholder', 'Search')}</Label>
                <AriaButton onPress={onClickSearchIcon} className="flex h-full items-center justify-center nnormal:order-1 nnormal:px-1.5">
                  <img src="/assets-new/search.svg" width={17} height={17} alt="Search" onClick={onClickSearchIcon} />
                </AriaButton>
                <Input
                  placeholder={t('generico', 'input.placeholder', 'Search')}
                  className="h-full w-full bg-transparent pl-1 outline-none nnormal:pl-0 nnormal:placeholder:text-xs nnormal:placeholder:uppercase nnormal:placeholder:text-[#9ca3af] camper:italic camper:text-[#919191]"
                />
              </TextField>

              <Button
                variant="text"
                className={`${autosuggestVisible ? 'visible' : 'invisible'} absolute right-0 top-[10px] h-[17px] w-[17px] nnormal:hidden ${autosuggestVisible ? 'transition-all delay-75' : ''} mobileXXL:right-[-18px] md:right-[-18px] lg:right-[-12px]`}
                onPress={() => {
                  if (isMobile) {
                    onClickExpandIcon();
                  } else {
                    setAutosuggestVisible(false);
                  }
                }}
              >
                <CloseSVG width="17" height="17" />
              </Button>
              {hasPersonalShoppper ?
                <PersonalShopper autosuggestVisible={autosuggestVisible} />
              : null}
            </div>
            <AutoSuggest query={value} data={unbxdSearchResults} visible={autosuggestVisible} setVisible={setAutosuggestVisible} />
          </div>
        </>
      : null}
    </div>
  );
}

SearchTNM.propTypes = {};
